/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getSelectedIds } from 'src/luis/appShell/buildPane/intentPage/IntentPage';
import { IntentPageUIConfigStore } from 'src/luis/appShell/buildPane/intentPage/stores/IntentPageUIConfigStore';
import { Pattern } from 'src/luis/modules/patterns/Pattern';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { AddAsPatternModal } from 'src/luis/projects/utterances/components/AddAsPatternModal/AddAsPatternModal';
import { useUtteranceActions } from 'src/luis/projects/utterances/hooks/useUtteranceActions';
import { UtteranceWithAnalytics } from 'src/luis/projects/utterances/types/UtteranceWithAnalytics';
import { Model } from 'src/luis/types/Model';
import { ModelType } from 'src/luis/types/ModelType';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { CreateEntityModal } from 'src/luis/verticals/createEntityModal/CreateEntityModal';
import { EditUtteranceModal } from 'src/luis/verticals/entityDetails/components/entityExamples/EditUtteranceModal';

export type IntentPageOpsModalsProps = {
    /**
     * The intent page configuration store.
     */
    pageConfigsStore: IntentPageUIConfigStore;

    /**
     * The current intent objects.
     */
    model: Omit<Model, 'name'>;

    /**
     * The available entity stores for the current version.
     */
    entities: Entity[];

    /**
     * The available utterance stores for the current intent.
     */
    utteranceStores: UtteranceWithAnalytics[];

    /**
     * The activity source for logging purposes.
     */
    activitySource: string;
};

const messages = defineMessages({
    deleteUtteranceModalTitle: {
        id: 'intentPage.intentPageOpsModals.deleteUtteranceModalTitle',
        defaultMessage: 'Delete selected utterance(s)?'
    },
    deleteUtteranceModalDescription: {
        id: 'intentPage.intentPageOpsModals.deleteUtteranceModalDescription',
        defaultMessage: 'Are you sure you want to delete the selected utterance(s)?'
    },
    addingPatternsRunning: {
        id: 'intentPage.intentPageOpsModals.addingPatternsRunning',
        defaultMessage: 'Adding patterns...'
    },
    addingPatternsComplete: {
        id: 'intentPage.intentPageOpsModals.addingPatternsComplete',
        defaultMessage: 'Adding patterns completed'
    },
    addingPatternsFailing: {
        id: 'intentPage.intentPageOpsModals.addingPatternsFailing',
        defaultMessage: 'Adding patterns failed'
    },
    editUtteranceAccessibilityConfirmation: {
        id: 'intentPage.intentPageOpsModals.editUtteranceAccessibilityConfirmation',
        defaultMessage: 'Successfully modified utterance'
    }
});

/**
 * Represents the intent page modals.
 */
export const IntentPageOpsModals = observer((props: IntentPageOpsModalsProps) => {
    const { pageConfigsStore, model, entities, activitySource, utteranceStores } = props;
    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();
    const { deleteUtterances, addLabel, editUtterance } = useUtteranceActions({ model });
    const logger = React.useRef(getEventLogger(activitySource)).current;
    const { announceAction } = useAnnounced();

    const { labelingStore, opsStore } = pageConfigsStore;

    const defaultTracker = useProgressTracker({
        onComplete: () => {
            transaction(() => {
                pageConfigsStore.selection.setAllSelected(false);
                opsStore.setSelectedUtteranceId(null);
                opsStore.resetModalsConfigs();
            });
        }
    });

    const editUtteranceTracker = useProgressTracker({
        onComplete: () => {
            transaction(() => {
                announceAction(localize(messages.editUtteranceAccessibilityConfirmation));
                pageConfigsStore.selection.setAllSelected(false);
                opsStore.resetModalsConfigs();
                opsStore.setSelectedUtteranceId(null);
                opsStore.setIsUtteranceEditModeOn(false);
            });
        }
    });

    const handleDelete = React.useCallback(() => {
        deleteUtterances(
            opsStore.selectedUtteranceId ? [opsStore.selectedUtteranceId] : getSelectedIds(pageConfigsStore.selection),
            defaultTracker
        );
    }, [deleteUtterances, pageConfigsStore]);

    const onAddAsPatterns = (patterns: Pattern[]) => {
        patterns.forEach(({ labeledIntent: { id: intentId }, text }) => {
            dispatcher.dispatch('createPattern', {
                intentId: intentId as string,
                text,
                tracker: defaultTracker,
                toastData: {
                    running: { title: localize(messages.addingPatternsRunning) },
                    completed: { title: localize(messages.addingPatternsComplete) },
                    failed: { title: localize(messages.addingPatternsFailing) }
                }
            });
        });
    };

    const onCreateEntity = (entityStore: EntityStore) => {
        transaction(() => {
            if (labelingStore.tokenSelectionMetadata && entityStore && entityStore.id && isMachineLearned(entityStore.typeId)) {
                addLabel(
                    labelingStore.tokenSelectionMetadata.utteranceId,
                    {
                        entityId: entityStore.id,
                        startTokenIndex: labelingStore.tokenSelectionMetadata.selection[0],
                        endTokenIndex: labelingStore.tokenSelectionMetadata.selection[1]
                    },
                    'createNewEntity'
                );

                labelingStore.setTokenSelectionMetadata(null);
            }
            opsStore.setModalsConfigState({ isEntityModalOpen: false });
            logger.end('CreateEntityDialog');
        });
    };

    const onCreateEntityDismiss = () => {
        opsStore.setModalsConfigState({ isEntityModalOpen: false });
        logger.log('CreateEntityDialogDismissed');
    };

    const onEditUtteranceModalSubmit = (doNotShowAgain: boolean) => {
        const selectedUtterance = utteranceStores.find(u => u.utteranceStore.id === opsStore.selectedUtteranceId);

        editUtterance(selectedUtterance.utteranceStore.id, selectedUtterance.utteranceStore.text, editUtteranceTracker);
        if (doNotShowAgain) {
            dispatcher.dispatch('hideEditUtteranceDisclaimerModal');
        }
    };

    return (
        <>
            <DeletePromptModal
                hidden={!opsStore.modalsConfigState.isDeleteModalOpen}
                onDelete={handleDelete}
                onDismiss={() => opsStore.setModalsConfigState({ isDeleteModalOpen: false })}
                dialogContentProps={{
                    title: localize(messages.deleteUtteranceModalTitle),
                    subText: localize(messages.deleteUtteranceModalDescription)
                }}
                disabled={defaultTracker.isRunning}
            />

            <AddAsPatternModal
                hidden={!opsStore.modalsConfigState.isAddToPatternsModalOpen}
                selectedUtteranceObservable={opsStore.selectedItems.length && opsStore.selectedItems[0]}
                entities={entities}
                onSubmit={({ patterns }) => onAddAsPatterns(patterns)}
                onDismiss={() => opsStore.setModalsConfigState({ isAddToPatternsModalOpen: false })}
                disabled={defaultTracker.isRunning}
            />

            <CreateEntityModal
                entityName={opsStore.newEntityCreationName}
                allowedModelTypes={[ModelType.Simple, ModelType.List]}
                hidden={!opsStore.modalsConfigState.isEntityModalOpen}
                onSubmit={onCreateEntity}
                onDismiss={onCreateEntityDismiss}
                token={labelingStore.tokenSelectionMetadata?.selectedText}
            />

            <EditUtteranceModal
                hidden={!opsStore.modalsConfigState.isEditUtteranceModalOpen}
                disabled={editUtteranceTracker.isRunning}
                onDismiss={() => opsStore.setModalsConfigState({ isEditUtteranceModalOpen: false })}
                onSubmit={onEditUtteranceModalSubmit}
            />
        </>
    );
});
